import { useEffect, useState, useCallback } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useToast, humanizeError } from '../shared/Toast'
import { DAY_LABELS, DEFAULT_TEMPLATES } from '../../lib/tasks'
import { inviteMember, isEmail, randomToken, formatShiftHours } from '../../lib/invite'

const SHIFT_CHOICES = [
  { value: 'morning', label: 'Morning' },
  { value: 'afternoon', label: 'Afternoon' },
  { value: 'evening', label: 'Evening' },
  { value: 'night', label: 'Night' },
  { value: 'any', label: 'Flexible' },
]

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7]

/**
 * Seed tasks for a new caregiver assignment.
 *
 * For a fixed shift only the defaults that belong to that shift are seeded —
 * putting "Bedtime routine" on a morning shift would just be noise in the
 * caregiver's checklist. A flexible caregiver gets all seven at their natural
 * shifts, so the right ones surface whenever they work.
 */
function seedTasksFor(shift) {
  if (shift === 'any') return DEFAULT_TEMPLATES.map((t) => ({ ...t }))
  const matching = DEFAULT_TEMPLATES.filter((t) => t.shift === shift)
  return (matching.length ? matching : DEFAULT_TEMPLATES).map((t) => ({ ...t, shift }))
}

/**
 * Every caregiver across every client this coordinator runs.
 *
 * care_team_members rows are per client, so the same person appears once per
 * assignment — they're grouped here by account (or invited email, before they
 * accept) so the list reads as people rather than rows.
 */
export default function CaregiverList({ adminMemberships }) {
  const { user, profile } = useAuth()
  const toast = useToast()

  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [sheet, setSheet] = useState(null) // null | { mode: 'add' } | { mode: 'edit', caregiver }

  const clients = adminMemberships.map((m) => m.recipient)
  const ids = clients.map((c) => c.id)

  const load = useCallback(async () => {
    if (!ids.length) { setLoading(false); return }
    const { data } = await supabase
      .from('care_team_members')
      .select('id, care_recipient_id, user_id, invited_name, invited_email, relationship, schedule_json, profile:profiles(id, full_name, email)')
      .in('care_recipient_id', ids)
      .eq('role', 'caregiver')
      .eq('is_active', true)

    const byPerson = new Map()
    for (const m of data || []) {
      const key = m.user_id || (m.invited_email || '').toLowerCase() || m.id
      const client = clients.find((c) => c.id === m.care_recipient_id)
      if (!byPerson.has(key)) {
        byPerson.set(key, {
          key,
          name: m.profile?.full_name || m.invited_name || m.invited_email || 'Caregiver',
          email: m.profile?.email || m.invited_email || null,
          accepted: !!m.user_id,
          relationship: m.relationship || null,
          schedule: null,
          assignments: [],
        })
      }
      const entry = byPerson.get(key)
      entry.accepted = entry.accepted || !!m.user_id
      // Hours are per assignment; the card shows the first one that has them.
      if (!entry.schedule && m.schedule_json) entry.schedule = m.schedule_json
      entry.assignments.push({ membershipId: m.id, client, schedule: m.schedule_json || null })
    }
    setRows([...byPerson.values()])
    setLoading(false)
  }, [adminMemberships]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load() }, [load])

  async function unassign(membershipId) {
    if (!window.confirm('Remove this caregiver from that client?')) return
    const { error } = await supabase.from('care_team_members')
      .update({ is_active: false }).eq('id', membershipId)
    if (error) return toast.error(humanizeError(error))
    await load()
    toast.success('Caregiver unassigned.')
  }

  return (
    <div id="caregivers" style={{ scrollMarginTop: 72 }}>
      <div className="spread" style={{ marginBottom: 8 }}>
        <div className="section-title">Our caregivers</div>
        <button className="btn btn-sm btn-secondary" onClick={() => setSheet({ mode: 'add' })}>
          + Add caregiver
        </button>
      </div>

      {loading ? (
        <div className="card muted" style={{ fontSize: 14 }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card muted" style={{ fontSize: 14, lineHeight: 1.55 }}>
          No caregivers yet. Invite one and they&rsquo;ll see a simplified view with
          just their shift tasks.
        </div>
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {rows.map((c) => (
            <div key={c.key} className="card">
              <div className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
                <div
                  aria-hidden
                  style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--pa)', color: '#fff', fontWeight: 700,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >
                  {(c.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="spread">
                    <div style={{ fontWeight: 700 }}>{c.name}</div>
                    <div className="row" style={{ gap: 8, flexShrink: 0 }}>
                      <span className={`badge ${c.accepted ? 'badge-green' : 'badge-amber'}`}>
                        {c.accepted ? 'Active' : 'Invited'}
                      </span>
                      <button
                        onClick={() => setSheet({ mode: 'edit', caregiver: c })}
                        style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: 13 }}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                  {formatShiftHours(c.schedule?.shift_start, c.schedule?.shift_end) && (
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--teal)' }}>
                      {formatShiftHours(c.schedule.shift_start, c.schedule.shift_end)}
                    </div>
                  )}
                  {c.email && (
                    <div className="muted" style={{ fontSize: 13 }}>{c.email}</div>
                  )}
                  <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
                    {c.assignments.length
                      ? c.assignments
                          .map((a) => a.client?.preferred_name || a.client?.full_name)
                          .filter(Boolean)
                          .join(' · ')
                      : 'No clients assigned'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {sheet && (
        <CaregiverSheet
          mode={sheet.mode}
          caregiver={sheet.caregiver}
          clients={clients}
          userId={user.id}
          inviterName={profile?.full_name || 'A coordinator'}
          onUnassign={unassign}
          onCancel={() => setSheet(null)}
          onDone={() => { setSheet(null); load() }}
        />
      )}
    </div>
  )
}

function CaregiverSheet({ mode, caregiver, clients, userId, inviterName, onUnassign, onCancel, onDone }) {
  const toast = useToast()
  const editing = mode === 'edit'

  const [name, setName] = useState(caregiver?.name || '')
  const [email, setEmail] = useState(caregiver?.email || '')
  const [picked, setPicked] = useState([])
  const [shift, setShift] = useState('morning')
  const [shiftStart, setShiftStart] = useState('')
  const [shiftEnd, setShiftEnd] = useState('')
  const [days, setDays] = useState([...ALL_DAYS])
  const [busy, setBusy] = useState(false)

  // When editing, only clients they aren't already on can be added.
  const assignedIds = new Set((caregiver?.assignments || []).map((a) => a.client?.id))
  const assignable = editing ? clients.filter((c) => !assignedIds.has(c.id)) : clients

  const toggleDay = (d) =>
    setDays((l) => (l.includes(d) ? l.filter((x) => x !== d) : [...l, d]))
  const togglePick = (id) =>
    setPicked((l) => (l.includes(id) ? l.filter((x) => x !== id) : [...l, id]))

  async function submit() {
    if (!name.trim()) return toast.error("Enter the caregiver's name.")
    if (!isEmail(email)) return toast.error('Enter a valid email address.')
    if (!picked.length) return toast.error('Pick at least one client.')
    if (!days.length) return toast.error('Pick at least one day.')

    setBusy(true)
    try {
      const sortedDays = [...days].sort((a, b) => a - b)
      const schedule = {
        shift,
        shift_start: shiftStart || null,
        shift_end: shiftEnd || null,
        days: sortedDays,
      }
      const seeds = seedTasksFor(shift)

      // One token, one email — the caregiver clicks once and every selected
      // client links. accept_invite claims sibling invites for the same
      // address (see 20260803_accept_invite_all.sql). invite_token is UNIQUE,
      // so each row still needs its own value.
      let firstUrl = null
      let emailed = false
      const failed = []

      for (let i = 0; i < picked.length; i++) {
        const target = clients.find((c) => c.id === picked[i])
        if (!target) continue
        const clientName = target.preferred_name || target.full_name

        const res = await inviteMember({
          recipientId: target.id,
          recipientName: clientName,
          name, email,
          role: 'caregiver',
          relationship: shift === 'any' ? 'Caregiver' : `${shift} caregiver`,
          inviterName,
          scheduleJson: schedule,
          token: randomToken(),
          sendEmail: i === 0, // only the first invite emails
        })
        if (!res.ok) { failed.push(clientName); continue }
        if (i === 0) { firstUrl = res.inviteUrl; emailed = res.emailed }

        const { error: tplErr } = await supabase.from('task_templates').insert(
          seeds.map((t, n) => ({
            care_recipient_id: target.id,
            created_by: userId,
            title: t.title,
            shift: t.shift,
            days_of_week: sortedDays,
            assigned_role: 'caregiver',
            sort_order: n,
            is_active: true,
          }))
        )
        if (tplErr) toast.error(`Assigned to ${clientName}, but the task schedule could not be saved.`)
      }

      if (failed.length === picked.length) {
        throw new Error('Could not create the invitation.')
      }
      if (failed.length) {
        toast.error(`Could not assign to: ${failed.join(', ')}.`)
      }

      if (!emailed) {
        // eslint-disable-next-line no-console
        console.log('Invite link:', firstUrl)
        toast.error('Invitation created, but the email could not be sent — link logged to console.')
      } else {
        toast.success(`Invitation sent to ${name.trim()}. They'll see their tasks once they accept.`)
      }
      onDone()
    } catch (err) {
      toast.error(humanizeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && !busy && onCancel()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
    >
      <div style={{ background: 'var(--bg)', width: '100%', maxWidth: 'var(--maxw)', borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, maxHeight: '92dvh', overflowY: 'auto' }}>
        <div className="spread" style={{ marginBottom: 12 }}>
          <h2 className="title" style={{ fontSize: 20 }}>
            {editing ? caregiver.name : 'Add caregiver'}
          </h2>
          <button onClick={onCancel} aria-label="Close" disabled={busy} style={{ background: 'none', border: 'none', fontSize: 22, color: 'var(--muted)' }}>✕</button>
        </div>

        <div className="stack">
          {editing && (
            <div className="card stack" style={{ gap: 8 }}>
              <div className="section-title">Assigned clients</div>
              {caregiver.assignments.length === 0 ? (
                <p className="muted" style={{ fontSize: 14, margin: 0 }}>None yet.</p>
              ) : (
                caregiver.assignments.map((a) => (
                  <div key={a.membershipId} className="spread">
                    <span>{a.client?.preferred_name || a.client?.full_name || 'Unknown client'}</span>
                    <button
                      onClick={() => onUnassign(a.membershipId)}
                      style={{ background: 'none', border: 'none', color: 'var(--danger)', fontSize: 13 }}
                    >
                      Unassign
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="field">
            <label className="label" htmlFor="cg-name">Name *</label>
            <input id="cg-name" className="input" value={name} disabled={editing}
              onChange={(e) => setName(e.target.value)} placeholder="e.g. Priya" />
          </div>

          <div className="field">
            <label className="label" htmlFor="cg-email">Email *</label>
            <input id="cg-email" className="input" type="email" inputMode="email"
              value={email} disabled={editing}
              onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
          </div>

          <div className="field">
            <span className="label">
              {editing ? 'Assign to more clients' : 'Assign to clients'}
            </span>
            {assignable.length === 0 ? (
              <p className="muted" style={{ fontSize: 14, margin: 0 }}>
                {editing ? 'Already assigned to every client.' : 'No clients yet — add one first.'}
              </p>
            ) : (
              <div className="stack" style={{ gap: 4 }}>
                {assignable.map((c) => (
                  <label key={c.id} className="row" style={{ gap: 10, cursor: 'pointer', minHeight: 40 }}>
                    <input
                      type="checkbox"
                      checked={picked.includes(c.id)}
                      onChange={() => togglePick(c.id)}
                      style={{ width: 20, height: 20, flexShrink: 0 }}
                    />
                    <span className="grow">{c.preferred_name || c.full_name}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="field">
            <span className="label">Their shift</span>
            <div className="row wrap" style={{ gap: 8 }}>
              {SHIFT_CHOICES.map((s) => (
                <button key={s.value} type="button"
                  className={`chip${shift === s.value ? ' chip-on' : ''}`}
                  aria-pressed={shift === s.value}
                  onClick={() => setShift(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="label">
              Shift hours <span className="hint">— optional</span>
            </span>
            <div className="row" style={{ gap: 10 }}>
              <input className="input grow" type="time" value={shiftStart}
                onChange={(e) => setShiftStart(e.target.value)} aria-label="Shift start time" />
              <span className="muted" style={{ fontWeight: 700 }}>–</span>
              <input className="input grow" type="time" value={shiftEnd}
                onChange={(e) => setShiftEnd(e.target.value)} aria-label="Shift end time" />
            </div>
          </div>

          <div className="field">
            <span className="label">Days they work</span>
            <div className="row" style={{ gap: 6 }}>
              {DAY_LABELS.map((d) => {
                const on = days.includes(d.value)
                return (
                  <button key={d.value} type="button" onClick={() => toggleDay(d.value)}
                    aria-pressed={on} aria-label={d.label}
                    style={{
                      width: 36, height: 36, borderRadius: '50%', fontWeight: 700, fontSize: 13,
                      border: on ? '2px solid var(--teal)' : '1.5px solid var(--border)',
                      background: on ? 'var(--teal-light)' : 'var(--surface)',
                      color: on ? 'var(--teal-dark)' : 'var(--muted)', cursor: 'pointer',
                    }}>
                    {d.short}
                  </button>
                )
              })}
            </div>
          </div>

          <p className="muted" style={{ fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            They&rsquo;ll get a simplified view with just their tasks and shift log.
            A starter checklist is created for the shift you pick.
          </p>

          <button className="btn btn-lg btn-block" onClick={submit}
            disabled={busy || !picked.length || !name.trim() || !email.trim()}>
            {busy ? <span className="spinner" /> : editing ? `Assign to ${picked.length || ''} client${picked.length === 1 ? '' : 's'}` : 'Send invitation'}
          </button>
        </div>
        <div style={{ height: 8 }} />
      </div>
    </div>
  )
}
